import React, { useState, useMemo, useEffect } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  Badge,
  Button,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContextNew';
import { useRole } from '../hooks/useRole';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { useOrganizationPermissions } from '../hooks/useOrganizationPermissions';
import { colors } from '../theme/variables';
import SafeAvatar from './SafeAvatar';
import LanguageSwitcher from './LanguageSwitcher';
import athletoLogoLight from '../assets/athleto_new.png';
import athletoLogoDark from '../assets/athleto_new_dark.png';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { SocialService } from '../services/socialService';

// Icons
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ChatIcon from '@mui/icons-material/Chat';
import CampaignIcon from '@mui/icons-material/Campaign';
import SupervisoryAccountIcon from '@mui/icons-material/SupervisorAccount';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShieldIcon from '@mui/icons-material/Shield';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FlagIcon from '@mui/icons-material/Flag';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SportsIcon from '@mui/icons-material/Sports';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CodeIcon from '@mui/icons-material/Code';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MapIcon from '@mui/icons-material/Map';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ParentLinkService } from '../services/parentLinkService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { userPath } from '../utils/dbPaths';
import { UserProfile } from '../types/user';
import { getEffectivePremiumAccess } from '../utils/billingAccess';
import {
  ORG_ADMIN_BASE,
  ORG_ADMIN_SECTIONS,
  orgNavSections,
  orgSectionNavLabel,
  orgSectionNavLabelKey,
  orgSectionPath,
} from '../utils/orgConsoleNav';

const SIDEBAR_WIDTH_OPEN = 260;
const SIDEBAR_WIDTH_CLOSED = 60;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  onLogout: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({
  open,
  onToggle,
  onLogout,
  isMobile = false,
  mobileOpen = false,
  onMobileClose
}: SidebarProps) {
  const theme = useTheme();
  const { t } = useTranslation('nav');
  const isDark = theme.palette.mode === 'dark';
  const sidebarExpanded = isMobile || open;
  const { user, effectiveUserProfile, effectiveUserId } = useUser();
  const { permissions } = useRole();
  const { canAccessAiAnalysis, isOrgAdmin, primaryOrganization } = useOrganizationPermissions();
  const { profile: effectiveProfile } = useEffectiveUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [orgAdminExpanded, setOrgAdminExpanded] = useState(
    location.pathname.startsWith('/org-admin')
  );
  const [adminExpanded, setAdminExpanded] = useState(
    location.pathname.startsWith('/admin')
  );
  const [organizationsExpanded, setOrganizationsExpanded] = useState(
    location.pathname.startsWith('/organizations')
  );
  const [districtExpanded, setDistrictExpanded] = useState(
    location.pathname.startsWith('/district-admin')
  );

  // Collapsible nav sections, keyed by item name. A lookup rather than a
  // ternary chain so adding a section is one line and cannot fall through to
  // another section's state.
  const sectionExpansion: Record<string, [boolean, (value: boolean) => void]> = {
    'School Admin': [orgAdminExpanded, setOrgAdminExpanded],
    Admin: [adminExpanded, setAdminExpanded],
    Organizations: [organizationsExpanded, setOrganizationsExpanded],
    'District Admin': [districtExpanded, setDistrictExpanded],
  };
  const isSectionExpanded = (name: string) => sectionExpansion[name]?.[0] ?? false;
  const toggleSection = (name: string) => {
    const entry = sectionExpansion[name];
    if (entry) entry[1](!entry[0]);
  };
  // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
  // const [recruiterExpanded, setRecruiterExpanded] = useState(
  //   location.pathname.startsWith('/recruiter')
  // );
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [linkedChildren, setLinkedChildren] = useState<UserProfile[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);

  // Check if user is a parent
  const isParent = effectiveUserProfile?.role === 'parent';

  const premiumAccess = useMemo(() => {
    if (!primaryOrganization) return { hasPremium: false, isPending: false, billingStatus: null };
    return getEffectivePremiumAccess(primaryOrganization);
  }, [primaryOrganization]);

  // V1 Privacy: Account type removed - all users are org-bound

  // Subscribe to notifications for unread count
  useEffect(() => {
    if (effectiveUserId) {
      const unsubscribe = SocialService.subscribeToUserNotifications(
        effectiveUserId,
        (notifications) => {
          setUnreadCount(notifications.filter(n => !n.read).length);
        }
      );

      return () => {
        try {
          unsubscribe();
        } catch (e) {        }
      };
    }
  }, [effectiveUserId]);

  // Load linked children for parents
  useEffect(() => {
    const loadLinkedChildren = async () => {
      if (!effectiveUserId || !isParent) {
        setLinkedChildren([]);
        return;
      }

      // Check if user has linked_children_ids
      const childIds = effectiveUserProfile?.linked_children_ids;
      if (!childIds || childIds.length === 0) {
        setLinkedChildren([]);
        return;
      }

      setLoadingChildren(true);
      try {
        const childProfiles: UserProfile[] = [];
        for (const childId of childIds) {
          const childDoc = await getDoc(doc(db, userPath(childId)));
          if (childDoc.exists()) {
            childProfiles.push({ uid: childId, ...childDoc.data() } as UserProfile);
          }
        }
        setLinkedChildren(childProfiles);
        // Auto-expand first child if none expanded
        if (childProfiles.length > 0 && !expandedChildId) {
          setExpandedChildId(childProfiles[0].uid);
        }

        // If parent has linked children, ensure claims are synced
        // This helps fix the case where claims weren't updated when child was linked
        if (childProfiles.length > 0 && user) {
          try {
            const tokenResult = await user.getIdTokenResult();
            const claimsChildren = (tokenResult.claims.linked_children as string[]) || [];

            // If claims don't match Firestore, refresh them
            if (claimsChildren.length !== childIds.length ||
                !childIds.every(id => claimsChildren.includes(id))) {             // Call cloud function to refresh claims
              const { refreshUserClaims } = await import('../services/cloudFunctions');
              await refreshUserClaims();

              // Force token refresh
              await user.getIdToken(true);            }
          } catch (claimsError) {          }
        }
      } catch (error) {      } finally {
        setLoadingChildren(false);
      }
    };

    loadLinkedChildren();
  }, [effectiveUserId, isParent, effectiveUserProfile?.linked_children_ids, user]);

  // Icon per school-admin console section. Overview carries the school logo
  // when there is one, which is the sidebar's only branding.
  const ORG_ADMIN_SECTION_ICONS: Record<string, React.ReactNode> = useMemo(
    () => ({
      overview: primaryOrganization?.branding?.logo_url ? (
        <Box
          component="img"
          src={primaryOrganization.branding.logo_url}
          alt={t('sidebar.orgLogoAlt', { name: primaryOrganization.name })}
          sx={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 0.5 }}
        />
      ) : (
        <BusinessIcon />
      ),
      members: <GroupIcon />,
      invitations: <PersonAddIcon />,
      teams: <Diversity3Icon />,
      monitoring: <ShieldIcon />,
      activity: <HistoryIcon />,
      settings: <SettingsIcon />,
    }),
    [primaryOrganization?.branding?.logo_url, primaryOrganization?.name, t],
  );

  const navItems = useMemo(() => {
    const items = [
      { name: 'Teams', label: t('sidebar.teams'), path: '/teams', icon: <GroupIcon />, requireAuth: true },
      { name: 'Messages', label: t('sidebar.messages'), path: '/messages', icon: <ChatIcon />, requireAuth: true },
      { name: 'Announcements', label: t('sidebar.announcements'), path: '/announcements', icon: <CampaignIcon />, requireAuth: true },
      { name: 'Notifications', label: t('sidebar.notifications'), path: '/notifications', icon: <NotificationsIcon />, requireAuth: true, showBadge: true },
      // COMMENTED OUT FOR FIRST RELEASE - Analysis feature
      // { name: 'Analysis', path: '/analysis', icon: <AssessmentIcon />, requireAuth: true, permission: 'can_access_analysis' },
      { name: 'Challenges', label: t('sidebar.challenges'), path: '/challenges', icon: <EmojiEventsIcon />, requireAuth: true, permission: 'can_access_challenges' },
      // COMMENTED OUT - Connections moved to profile or separate page
      // { name: 'Connections', path: '/connections', icon: <PersonAddIcon />, requireAuth: true, accountTypeRequired: 'public' as AccountType },
      // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
      // {
      //   name: 'Recruiting',
      //   path: '/recruiter',
      //   icon: <PersonSearchIcon />,
      //   requireAuth: true,
      //   roleRequired: 'recruiter',
      //   expandable: true,
      //   subItems: [
      //     { name: 'Dashboard', path: '/recruiter/dashboard', icon: <DashboardIcon /> },
      //     { name: 'Discover Athletes', path: '/recruiter/discover', icon: <PersonSearchIcon /> },
      //     { name: 'Following', path: '/recruiter/following', icon: <StarIcon /> },
      //     { name: 'Teams', path: '/recruiter/teams', icon: <GroupIcon /> },
      //   ]
      // },
      {
        name: 'Developer',
        label: t('sidebar.developer'),
        path: '/developer/environment',
        icon: <CodeIcon />,
        requireAuth: true,
      },
      {
        name: 'District Admin',
        label: t('sidebar.districtAdmin'),
        path: '/district-admin',
        icon: <SchoolIcon />,
        requireAuth: true,
        expandable: true,
        subItems: [
          { name: 'Overview', label: t('sidebar.sections.districtAdmin.overview'), path: '/district-admin', icon: <SchoolIcon /> },
          { name: 'Morning brief', label: t('sidebar.sections.districtAdmin.morningBrief'), path: '/district-admin/brief', icon: <AutoAwesomeIcon /> },
          { name: 'Directory', label: t('sidebar.sections.districtAdmin.directory'), path: '/district-admin/directory', icon: <GroupIcon /> },
          { name: 'Teams', label: t('sidebar.sections.districtAdmin.teams'), path: '/district-admin/teams', icon: <Diversity3Icon /> },
          { name: 'Announcements', label: t('sidebar.sections.districtAdmin.announcements'), path: '/district-admin/announcements', icon: <CampaignIcon /> },
          // Academic eligibility report (AUTOMATION_SPEC A11)
          { name: 'Academic eligibility', label: t('sidebar.sections.districtAdmin.academicEligibility'), path: '/district-admin/academic', icon: <VerifiedUserIcon /> },
          { name: 'Sync history', label: t('sidebar.sections.districtAdmin.syncHistory'), path: '/district-admin/sync', icon: <CloudSyncIcon /> },
          { name: 'Data health', label: t('sidebar.sections.districtAdmin.dataHealth'), path: '/district-admin/health', icon: <MonitorHeartIcon /> },
          { name: 'Integration', label: t('sidebar.sections.districtAdmin.integration'), path: '/district-admin/integration', icon: <CloudSyncIcon /> },
        ],
      },
      {
        name: 'School Admin',
        label: t('sidebar.schoolAdmin'),
        path: '/org-admin',
        icon: <SupervisoryAccountIcon />,
        requireAuth: true,
        permission: 'can_manage_organization_members',
        expandable: true,
        subItems: [
          // One entry per console section, from ORG_ADMIN_SECTIONS, so the
          // sidebar and the tab bars can never drift apart (same shape as the
          // district console above). The Overview entry keeps the school's
          // logo, which is the only branding the collapsed sidebar shows.
          // Labels resolve through the `console` namespace (NEXT_QUEUE_SPEC
          // §1.4) — batch C's registry, translated with batch C.
          ...orgNavSections(ORG_ADMIN_SECTIONS, { hasPremium: premiumAccess.hasPremium }).map(
            (section) => ({
              name: orgSectionNavLabel(section),
              label: t(orgSectionNavLabelKey(section), { defaultValue: orgSectionNavLabel(section) }),
              path: orgSectionPath(ORG_ADMIN_BASE, section.key),
              icon: ORG_ADMIN_SECTION_ICONS[section.key] ?? <BusinessIcon />,
            })
          ),
          { name: 'Eligibility', label: t('sidebar.sections.schoolAdmin.eligibility'), path: '/clearance', icon: <VerifiedUserIcon /> },
          { name: 'Morning brief', label: t('sidebar.sections.schoolAdmin.morningBrief'), path: '/org-admin/brief', icon: <AutoAwesomeIcon /> },
          { name: 'Participation Report', label: t('sidebar.sections.schoolAdmin.participationReport'), path: '/participation-report', icon: <AssessmentIcon /> },
        ]
      },
      {
        name: 'Organizations',
        label: t('sidebar.organizations'),
        path: '/organizations',
        icon: <BusinessIcon />,
        requireAuth: true,
        permission: 'can_manage_organizations',
        expandable: true,
        subItems: [
          { name: 'Organizations', label: t('sidebar.sections.organizations.list'), path: '/organizations/list', icon: <ListAltIcon /> },
        ]
      },
      {
        name: 'Admin',
        label: t('sidebar.admin'),
        path: '/admin',
        icon: <AdminPanelSettingsIcon />,
        requireAuth: true,
        permission: 'can_access_admin',
        expandable: true,
        subItems: [
          { name: 'Overview', label: t('sidebar.sections.admin.overview'), path: '/admin/overview', icon: <DashboardIcon /> },
          { name: 'User Management', label: t('sidebar.sections.admin.userManagement'), path: '/admin/users', icon: <VerifiedUserIcon /> },
          // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
          // { name: 'Recruiter Verification', path: '/admin/recruiter-verification', icon: <VerifiedUserIcon /> },
          { name: 'Maps API Usage', label: t('sidebar.sections.admin.mapsApiUsage'), path: '/admin/maps-usage', icon: <MapIcon /> },
          { name: 'Districts', label: t('sidebar.sections.admin.districts'), path: '/admin/districts', icon: <SchoolIcon /> },
        ]
      },
    ];

    return items.filter(item => {
      if (item.requireAuth && !user) return false;

      // Parents don't see regular navigation - they have their own child-focused sidebar
      if (isParent) return false;

      // V1 Privacy: Account type filtering removed - all users are org-bound

      if (item.name === 'Teams' || item.name === 'Messages') {
        return true;
      }

      if (item.name === 'Analysis') {
        // Analysis requires both permission AND premium access
        return canAccessAiAnalysis && premiumAccess.hasPremium;
      }

      if (item.name === 'Developer') {
        return effectiveUserProfile?.role === 'super_admin';
      }

      if (item.name === 'District Admin') {
        return (
          effectiveUserProfile?.role === 'district_admin' ||
          effectiveUserProfile?.role === 'super_admin'
        );
      }

      if (item.name === 'School Admin') {
        return isOrgAdmin || effectiveUserProfile?.role === 'super_admin';
      }

      // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
      // // Role-specific filtering for recruiter section
      // if ((item as any).roleRequired) {
      //   const roleRequired = (item as any).roleRequired;
      //   return effectiveUserProfile?.role === roleRequired || effectiveUserProfile?.role === 'super_admin';
      // }

      if (item.permission && !permissions[item.permission as keyof typeof permissions]) return false;
      return true;
    });
  }, [user, permissions, canAccessAiAnalysis, isOrgAdmin, effectiveUserProfile, isParent, premiumAccess, ORG_ADMIN_SECTION_ICONS, t]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  /**
   * Which entry of a collapsible section is lit. The deepest matching prefix
   * wins, so `/org-admin/settings/billing` lights Settings (and not Overview,
   * whose `/org-admin` parent is a prefix of everything in the section) and
   * `/district-admin/sync/<runId>` lights Sync history.
   */
  const isSubItemActive = (subItems: { path: string }[], path: string) => {
    const matches = subItems.filter(
      (item) =>
        location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    );
    if (matches.length === 0) return false;
    const deepest = matches.reduce((a, b) => (b.path.length > a.path.length ? b : a));
    return deepest.path === path;
  };

  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {    }
  };

  const drawerContent = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: isDark ? colors.background.paper : '#ffffff',
    }}>
      {/* Athleto Logo & Brand Section */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: sidebarExpanded ? 'flex-start' : 'center',
        gap: 1.5,
        borderBottom: isDark ? `1px solid ${colors.border.default}` : '1px solid #e0e0e0',
        minHeight: 64,
        flexDirection: sidebarExpanded ? 'row' : 'column',
      }}>
        {!isMobile && !sidebarExpanded && (
          <Tooltip title={t('sidebar.expandTooltip')} placement="right">
            <IconButton onClick={onToggle} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        )}
        {sidebarExpanded && (
          <>
            <Box
              component="img"
              {...({ src: theme.palette.mode === 'dark' ? athletoLogoDark : athletoLogoLight, alt: "Athleto" } as any)}
              sx={{
                height: 32,
                width: 'auto',
              }}
            />
            <Box sx={{ flex: 1 }} />
            {!isMobile && (
              <IconButton onClick={onToggle} size="small">
                <ChevronLeftIcon />
              </IconButton>
            )}
          </>
        )}
      </Box>

      <Divider />

      {isParent ? (
        /* Parent-specific sidebar content */
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Home: every child's next event, clearance and news on one page */}
          <List sx={{ py: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/home"
                onClick={handleNavClick}
                selected={location.pathname === '/home' && !location.search.includes('view=')}
                sx={{
                  minHeight: 48,
                  justifyContent: sidebarExpanded ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: colors.primary.alpha[10],
                    borderLeft: sidebarExpanded ? '4px solid' : 'none',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: sidebarExpanded ? 2 : 'auto',
                    justifyContent: 'center',
                    color: location.pathname === '/home' ? 'primary.main' : 'text.secondary',
                  }}
                >
                  <HomeIcon />
                </ListItemIcon>
                {sidebarExpanded && (
                  <ListItemText
                    primary={t('sidebar.parent.home')}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: location.pathname === '/home' ? 700 : 600 }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </List>

          {/* My Children Header */}
          {sidebarExpanded && (
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  fontSize: '0.7rem',
                }}
              >
                {t('sidebar.parent.myChildren')}
              </Typography>
            </Box>
          )}

          {/* Loading state */}
          {loadingChildren && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">{t('common:states.loading')}</Typography>
            </Box>
          )}

          {/* No children linked */}
          {!loadingChildren && linkedChildren.length === 0 && sidebarExpanded && (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <ChildCareIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('sidebar.parent.noChildrenLinked')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                component={Link}
                to="/profile?linkChild=true"
                onClick={handleNavClick}
                sx={{ borderRadius: 2 }}
              >
                {t('sidebar.parent.linkChild')}
              </Button>
            </Box>
          )}

          {/* Children Accordions */}
          {!loadingChildren && linkedChildren.length > 0 && (
            <List sx={{ py: 0 }}>
              {linkedChildren.map((child) => {
                const isExpanded = expandedChildId === child.uid;
                const childPath = `/profile/${child.uid}`;
                const isChildActive =
                  location.pathname.includes(child.uid) || location.search.includes(`child=${child.uid}`);
                const childView = (view: string) =>
                  location.pathname === '/home' &&
                  location.search.includes(`child=${child.uid}`) &&
                  location.search.includes(`view=${view}`);

                return (
                  <Box key={child.uid}>
                    {/* Child Accordion Header */}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => setExpandedChildId(isExpanded ? null : child.uid)}
                        sx={{
                          minHeight: 56,
                          px: 2,
                          backgroundColor: isChildActive
                            ? (isDark ? colors.primary.alpha[10] : 'rgba(0, 120, 212, 0.08)')
                            : 'transparent',
                          borderLeft: isChildActive ? '4px solid' : '4px solid transparent',
                          borderColor: isChildActive ? 'primary.main' : 'transparent',
                          '&:hover': {
                            backgroundColor: isDark ? colors.primary.alpha[5] : 'rgba(0, 120, 212, 0.04)',
                          },
                        }}
                      >
                        <Avatar
                          src={child.photo_url || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: sidebarExpanded ? 2 : 0,
                            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                            fontSize: '1rem',
                            fontWeight: 700,
                          }}
                        >
                          {child.display_name?.[0]?.toUpperCase() || 'C'}
                        </Avatar>
                        {sidebarExpanded && (
                          <>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: isChildActive ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {child.display_name || t('sidebar.parent.childFallbackName')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {child.metadata?.sport && (
                                  <>
                                    <SportsIcon sx={{ fontSize: 12 }} />
                                    {child.metadata.sport}
                                  </>
                                )}
                                {!child.metadata?.sport && child.role}
                              </Typography>
                            </Box>
                            {isExpanded ? (
                              <ExpandLess sx={{ color: 'text.secondary' }} />
                            ) : (
                              <ExpandMore sx={{ color: 'text.secondary' }} />
                            )}
                          </>
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Child's Menu Items */}
                    {sidebarExpanded && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{
                          backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                          borderLeft: `4px solid ${isDark ? colors.primary.alpha[20] : colors.primary.alpha[10]}`,
                          ml: 2,
                          mr: 1,
                          mb: 1,
                          borderRadius: '0 8px 8px 0',
                        }}>
                          {/* View Profile */}
                          <ListItemButton
                            component={Link}
                            to={childPath}
                            onClick={handleNavClick}
                            selected={location.pathname === childPath && !location.search}
                            sx={{
                              minHeight: 40,
                              pl: 2,
                              '&.Mui-selected': {
                                backgroundColor: isDark ? colors.primary.alpha[15] : 'rgba(0, 120, 212, 0.12)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                              <PersonIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('sidebar.parent.childSubnav.profile')}
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                            />
                          </ListItemButton>

                          {/* Schedule */}
                          <ListItemButton
                            component={Link}
                            to={`/home?child=${child.uid}&view=schedule`}
                            onClick={handleNavClick}
                            selected={childView('schedule')}
                            sx={{
                              minHeight: 40,
                              pl: 2,
                              '&.Mui-selected': {
                                backgroundColor: colors.primary.alpha[10],
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                              <CalendarMonthIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('sidebar.parent.childSubnav.schedule')}
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                            />
                          </ListItemButton>

                          {/* Eligibility */}
                          <ListItemButton
                            component={Link}
                            to={`/home?child=${child.uid}&view=eligibility`}
                            onClick={handleNavClick}
                            selected={childView('eligibility')}
                            sx={{
                              minHeight: 40,
                              pl: 2,
                              '&.Mui-selected': {
                                backgroundColor: colors.primary.alpha[10],
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                              <VerifiedUserIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('sidebar.parent.childSubnav.eligibility')}
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                            />
                          </ListItemButton>

                          {/* Teams */}
                          <ListItemButton
                            component={Link}
                            to={`/teams?child=${child.uid}`}
                            onClick={handleNavClick}
                            selected={location.pathname === '/teams' && location.search.includes(`child=${child.uid}`)}
                            sx={{
                              minHeight: 40,
                              pl: 2,
                              '&.Mui-selected': {
                                backgroundColor: isDark ? colors.primary.alpha[15] : 'rgba(0, 120, 212, 0.12)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                              <GroupIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                child.team_ids?.length
                                  ? t('sidebar.parent.childSubnav.teamsWithCount', { count: child.team_ids.length })
                                  : t('sidebar.parent.childSubnav.teams')
                              }
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                            />
                          </ListItemButton>

                          {/* Messages */}
                          <ListItemButton
                            component={Link}
                            to={`/messages?child=${child.uid}`}
                            onClick={handleNavClick}
                            selected={location.pathname === '/messages' && location.search.includes(`child=${child.uid}`)}
                            sx={{
                              minHeight: 40,
                              pl: 2,
                              '&.Mui-selected': {
                                backgroundColor: isDark ? colors.primary.alpha[15] : 'rgba(0, 120, 212, 0.12)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                              <ChatIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('sidebar.parent.childSubnav.messages')}
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                            />
                          </ListItemButton>
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </List>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Link Another Child Button */}
          {sidebarExpanded && linkedChildren.length > 0 && (
            <Box sx={{ px: 2, py: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAddIcon />}
                component={Link}
                to="/profile?linkChild=true"
                onClick={handleNavClick}
                sx={{
                  borderRadius: 2,
                  borderStyle: 'dashed',
                  color: 'text.secondary',
                  borderColor: isDark ? colors.border.default : 'rgba(0,0,0,0.23)',
                  '&:hover': {
                    borderStyle: 'dashed',
                    backgroundColor: isDark ? colors.primary.alpha[5] : 'rgba(0, 120, 212, 0.04)',
                  },
                }}
              >
                {t('sidebar.parent.linkAnotherChild')}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        /* Regular Navigation Items for non-parents */
        <List sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {navItems.map((item) => (
          <Box key={item.name}>
            {item.expandable ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection(item.name)}
                    selected={isActive(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: sidebarExpanded ? 'initial' : 'center',
                      px: 2.5,
                      '&.Mui-selected': {
                        backgroundColor: isDark ? colors.primary.alpha[10] : 'rgba(0, 120, 212, 0.12)',
                        borderLeft: sidebarExpanded ? '4px solid' : 'none',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 0,
                      mr: sidebarExpanded ? 2 : 'auto',
                      justifyContent: 'center',
                      color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {sidebarExpanded && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isActive(item.path) ? 700 : 600,
                          }}
                        />
                        {isSectionExpanded(item.name) ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
                {sidebarExpanded && (
                  <Collapse in={isSectionExpanded(item.name)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems?.map((subItem) => {
                        const subActive = isSubItemActive(item.subItems || [], subItem.path);
                        return (
                        <ListItem key={subItem.name} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={subItem.path}
                            onClick={handleNavClick}
                            selected={subActive}
                            sx={{
                              pl: 4,
                              minHeight: 40,
                              '&.Mui-selected': {
                                backgroundColor: isDark ? colors.primary.alpha[10] : 'rgba(0, 120, 212, 0.12)',
                                borderLeft: '4px solid',
                                borderColor: 'primary.main',
                              },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 0,
                              mr: 2,
                              color: subActive ? 'primary.main' : 'text.secondary',
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.label}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                fontWeight: subActive ? 700 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </>
            ) : (
              <ListItem disablePadding>
                <Tooltip title={!sidebarExpanded ? item.label : ''} placement="right">
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    onClick={handleNavClick}
                    selected={isActive(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: sidebarExpanded ? 'initial' : 'center',
                      px: 2.5,
                      '&.Mui-selected': {
                        backgroundColor: isDark ? colors.primary.alpha[10] : 'rgba(0, 120, 212, 0.12)',
                        borderLeft: sidebarExpanded ? '4px solid' : 'none',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 0,
                      mr: sidebarExpanded ? 2 : 'auto',
                      justifyContent: 'center',
                      color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    }}>
                      {(item as any).showBadge ? (
                        <Badge badgeContent={unreadCount} color="secondary">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {sidebarExpanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive(item.path) ? 700 : 600,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}
          </Box>
        ))}
        </List>
      )}

      <Divider />

      {/* Organization Info at Bottom - shown for all org members, clickable only for admins */}
      {!isParent && primaryOrganization && sidebarExpanded && (
        <Box
          onClick={isOrgAdmin ? () => navigate(orgSectionPath(ORG_ADMIN_BASE, 'overview')) : undefined}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: isOrgAdmin ? 'pointer' : 'default',
            transition: 'background 0.2s ease',
            ...(isOrgAdmin && {
              '&:hover': {
                bgcolor: isDark ? colors.primary.alpha[5] : 'rgba(0, 120, 212, 0.08)',
              },
            }),
          }}
        >
          {primaryOrganization.branding?.logo_url ? (
            <Box
              component="img"
              {...({ src: primaryOrganization.branding.logo_url, alt: primaryOrganization.name } as any)}
              sx={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: colors.gradients.primary,
              }}
            >
              {primaryOrganization.name[0]?.toUpperCase() || 'O'}
            </Avatar>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {primaryOrganization.name}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Language Switcher - hidden when collapsed to icons, like the rest of the footer */}
      {sidebarExpanded && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <LanguageSwitcher variant="icon" />
        </Box>
      )}

      {/* User Profile */}
      {sidebarExpanded && (
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            '&:hover': {
              bgcolor: isDark ? colors.primary.alpha[5] : 'rgba(0, 120, 212, 0.08)',
            },
          }}
          onClick={handleProfileMenuOpen}
        >
          <Avatar
            src={effectiveUserProfile?.photo_url || user?.photoURL || undefined}
            sx={{
              width: 36,
              height: 36,
              background: colors.gradients.primary,
            }}
          >
            {(effectiveUserProfile?.display_name?.[0] || user?.displayName?.[0] || user?.email?.[0] || 'U')}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {effectiveUserProfile?.display_name || user?.displayName || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Collapsed view - just profile icon */}
      {!sidebarExpanded && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Tooltip title={t('sidebar.profileTooltip')} placement="right">
            <IconButton onClick={handleProfileMenuOpen} size="small">
              <Avatar
                src={effectiveUserProfile?.photo_url || user?.photoURL || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  background: colors.gradients.primary,
                }}
              >
                {(effectiveUserProfile?.display_name?.[0] || user?.displayName?.[0] || user?.email?.[0] || 'U')}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          component={Link}
          to="/account"
          onClick={() => {
            handleProfileMenuClose();
            handleNavClick();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('sidebar.profileMenu.account')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('sidebar.profileMenu.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

  // Mobile drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH_OPEN,
            boxSizing: 'border-box',
            background: isDark ? colors.background.paper : '#ffffff',
            borderRight: isDark ? `1px solid ${colors.border.default}` : '1px solid #e0e0e0',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop drawer
  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: open ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          background: isDark ? colors.background.paper : '#ffffff',
          borderRight: isDark ? `1px solid ${colors.border.default}` : '1px solid #e0e0e0',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
